<script setup lang="ts">
import { useClipboard } from "@vueuse/core";
import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";

import { useStoreUserV2 } from "~/stores/storeUserV2";
import { useWeb3AuthStore } from "~/stores/storeWeb3auth";
import { ClipboardCopy, CircleCheck, SquareArrowOutUpRight } from "lucide-vue-next";

const userStore = useStoreUserV2();
const web3AuthStore = useWeb3AuthStore();

const { t } = useI18n();

const { copy } = useClipboard();

const copyTimeout = ref<{
  address: NodeJS.Timeout | undefined;
  pk: NodeJS.Timeout | undefined;
}>({ address: undefined, pk: undefined });

const allowedCUIDs = [
  "cmhyxiu6r000gdhhnd7h0zsoy",
  "cm2i2k0ca001n0fs65uemcirv",
  "clrte3nkv00010fs67h13fwva",
  "cm0kpy6rw019q0gs61d6j80it",
];

function copyText() {
  copy(userStore.walletAddress.toLocaleLowerCase());
  clearTimeout(copyTimeout.value.address);
  copyTimeout.value.address = setTimeout(() => {
    copyTimeout.value.address = undefined;
  }, 500);
}

async function copyPK() {
  const pk = await web3AuthStore.web3auth?.provider?.request({
    method: "private_key",
  });
  console.log("pk", pk);
  copy(pk as string);
  clearTimeout(copyTimeout.value.pk);
  copyTimeout.value.pk = setTimeout(() => {
    copyTimeout.value.pk = undefined;
  }, 2500);
}

function truncateAddress(address: string) {
  // Extract start and end portions
  const start = address.slice(0, address.length - 4);
  const end = address.slice(-4);

  return {
    start,
    end,
    full: address,
  };
}

const walletAddress = computed(() => {
  return truncateAddress(userStore.walletAddress.toLocaleLowerCase());
});
</script>

<template>
  <div>
    <div
      class="bg-slate-900 p-2 py-3 text-sm text-slate-400 mb-0.5 px-5 rounded-t-lg flex justify-between items-center"
    >
      <div>{{ t("_shared.wallet") }} Polygon</div>
      <a :href="`https://polygonscan.com/address/${userStore.walletAddress}`" target="_blank">
        <SquareArrowOutUpRight class="w-4 hover:text-white cursor-pointer" />
      </a>
    </div>

    <div class="gap-2 bg-slate-900 p-5 py-5 rounded-b-lg flex flex-col">
      <div
        class="flex bg-slate-950 rounded-lg p-3 items-center text-sm text-ellipsis justify-between gap-2 border border-transparent transition-all ease-in duration-200"
        :class="{
          ' border-green-80': copyTimeout.address,
        }"
      >
        <div class="text-slate-400 border-r border-slate-600 pr-2">Address</div>
        <div class="flex items-center w-full overflow-ellipsis overflow-hidden">
          <div class="overflow-ellipsis overflow-hidden">
            {{ walletAddress.start }}
          </div>
          <div class="text-nowrap">{{ walletAddress.end }}</div>
        </div>
        <div
          @click.stop.prevent="copyText"
          class="text-slate-400 items-center flex cursor-pointer hover:text-white transition-colors duration-200"
        >
          <CircleCheck v-if="copyTimeout.address" class="h-4 -mt-0.5 text-green-60" />
          <ClipboardCopy v-else class="h-4 -mt-0.5" />
        </div>
      </div>
      <div
        v-if="allowedCUIDs.includes(userStore.cuid)"
        @click.stop.prevent="copyPK"
        class="flex bg-slate-950 rounded-lg p-3 items-center text-sm text-ellipsis justify-between gap-2 border border-transparent transition-all ease-in duration-200"
        :class="{
          ' border-green-80': copyTimeout.pk,
        }"
      >
        <div
          class="flex items-center w-full overflow-ellipsis overflow-hidden text-xs text-slate-400"
        >
          Copy Private Key
        </div>
        <div
          class="text-slate-400 items-center flex cursor-pointer hover:text-white transition-colors duration-200"
        >
          <CircleCheck v-if="copyTimeout.pk" class="h-4 -mt-0.5 text-green-60" />
          <ClipboardCopy v-else class="h-4 -mt-0.5" />
        </div>
      </div>
    </div>
  </div>
</template>
